"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";

import { Container } from "@/src/components/ui/Container";
import Section from "@/src/components/ui/Section";
import { Button } from "@/src/components/ui/Button";
import { Heading, Text } from "@/src/components/ui/Typography";
import { TextField } from "@/src/components/ui/TextField";
import { TextareaField } from "@/src/components/ui/TextareaField";
import { Label } from "@/src/components/ui/Label";
import { Input } from "@/src/components/ui/Input";
import { cn } from "@/src/lib/utils";
import { paths } from "@/src/routes/paths";

import { budgetOptions } from "./data";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_MAX_LENGTH = 15;

const PROPOSAL_MIN_LENGTH = 25;

type FieldName = "fullName" | "email" | "phone" | "proposal";

// Allows digits, +, -, (, ) and single spaces (formatting like
// "+99 (498) 484-8420"). Consecutive spaces collapse to one, and only
// non-space characters count toward the 15-char cap — matching what the
// /api/contact backend itself checks once spaces are stripped before
// submission (see handleSubmit), so a fully-formatted number can never
// silently fail server-side validation for being "too long".
function sanitizePhone(raw: string) {
  const filtered = raw.replace(/[^\d+\-() ]/g, "").replace(/ {2,}/g, " ");

  let significant = 0;
  let result = "";

  for (const char of filtered) {
    if (char === " ") {
      result += char;
      continue;
    }
    if (significant >= PHONE_MAX_LENGTH) continue;
    significant += 1;
    result += char;
  }

  return result;
}

export function ContactForm() {
  const router = useRouter();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [proposal, setProposal] = useState("");
  const [budget, setBudget] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>(
    {},
  );
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const markTouched = (field: FieldName) => () =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  function fieldError(field: FieldName, value: string) {
    if (!(touched[field] || attemptedSubmit)) return undefined;
    if (!value.trim()) return "Field is required";
    if (field === "proposal" && value.trim().length < PROPOSAL_MIN_LENGTH)
      return `Proposal must be at least ${PROPOSAL_MIN_LENGTH} characters`;
    if (field === "email" && !EMAIL_PATTERN.test(value))
      return "The email doesn't look real";
    return undefined;
  }

  const isValid = Boolean(
    fullName.trim() &&
    email.trim() &&
    EMAIL_PATTERN.test(email) &&
    phone.trim() &&
    proposal.trim().length >= PROPOSAL_MIN_LENGTH,
  );

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAttemptedSubmit(true);
    if (!isValid) return;

    setSubmitting(true);
    setErrorMessage("");

    const selected = budgetOptions.find((option) => option.label === budget);

    const formData = new FormData(event.currentTarget);
    formData.set("full_name", fullName);
    formData.set("email", email);
    formData.set("phone", phone.replace(/\s/g, ""));
    formData.set("phoneCode", "");
    formData.set("company_website", "");
    formData.set("proposal", proposal);
    formData.set(
      "budget",
      selected ? `$${selected.max} - $${selected.min}` : "",
    );

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();

      if (data.status === "mail_sent") {
        router.push(paths.thankYou);
        return;
      }

      setErrorMessage(
        data.invalid_fields?.[0]?.message ??
          "Something went wrong. Please try again.",
      );
    } catch {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Section className="bg-white">
      <Container>
        <form
          onSubmit={handleSubmit}
          noValidate
          className="flex max-w-175 flex-col gap-14 sm:gap-16"
        >
          {/* Honeypot — humans never see or fill this; bots do. */}
          <div
            aria-hidden="true"
            className="absolute left-[-9999px] h-px w-px overflow-hidden"
          >
            <Label htmlFor="website_url">Website URL (leave blank)</Label>
            <Input
              type="text"
              name="website_url"
              id="website_url"
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div>
            <Heading as="h2" size="headingLG">
              1. Tell us about your company
            </Heading>

            <div className="mt-8 flex flex-col gap-8">
              <TextField
                label="Name"
                name="full_name"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                onBlur={markTouched("fullName")}
                error={fieldError("fullName", fullName)}
              />

              <TextField
                label="Email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={markTouched("email")}
                error={fieldError("email", email)}
              />

              <TextField
                label="Phone Number"
                name="phone"
                type="tel"
                inputMode="tel"
                required
                value={phone}
                onChange={(e) => setPhone(sanitizePhone(e.target.value))}
                onBlur={markTouched("phone")}
                error={fieldError("phone", phone)}
              />
            </div>
          </div>

          <div>
            <Heading as="h2" size="headingLG">
              2. What are you looking to work on?
            </Heading>

            <div className="mt-8 flex flex-col gap-8">
              <TextareaField
                label="Write your proposal"
                name="proposal"
                required
                value={proposal}
                onChange={(e) => setProposal(e.target.value)}
                onBlur={markTouched("proposal")}
                error={fieldError("proposal", proposal)}
              />

              <div className="flex flex-col gap-3 pt-6">
                <Text size="bodySM" className="text-neutral-500">
                  What&rsquo;s your budget?
                </Text>

                <div className="flex flex-wrap gap-2">
                  {budgetOptions.map((option) => (
                    <Button
                      key={option.label}
                      type="button"
                      unstyled
                      aria-pressed={budget === option.label}
                      onClick={() => setBudget(option.label)}
                      className={cn(
                        "rounded-(--radius-full) border px-4 py-2",
                        budget === option.label
                          ? "border-neutral-700 bg-neutral-700 text-neutral-50"
                          : "border-neutral-300 bg-transparent text-neutral-700 hover:border-neutral-500",
                      )}
                    >
                      <Text
                        as="span"
                        size="bodySMMedium"
                        className="text-inherit"
                      >
                        {option.label}
                      </Text>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Button type="submit" disabled={submitting} className="w-fit">
              {submitting ? "Submitting..." : "Submit"}
            </Button>
            {errorMessage && (
              <Text size="bodySM" className="text-error">
                {errorMessage}
              </Text>
            )}
          </div>
        </form>
      </Container>
    </Section>
  );
}
